/**
 * ExportData Page
 *
 * Allows users to export their baby's data as PDF or CSV.
 * All processing happens client-side (zero-knowledge).
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { babyApi } from '../services/api';
import { getBabyKey, decryptBabyProfile } from '../crypto';
import { EventType, ProfileType } from '../types';
import { useExport } from '../hooks/useExport';
import { subDays, subMonths, startOfDay, endOfDay } from 'date-fns';

interface DecryptedBaby {
  id: string;
  name: string;
  profileType: ProfileType;
  dateOfBirth?: Date;
  dueDate?: string;
}

const EVENT_TYPES: { type: EventType; label: string; icon: string }[] = [
  { type: EventType.FEED, label: 'Feeds', icon: '🍼' },
  { type: EventType.NAPPY, label: 'Nappies', icon: '🧷' },
  { type: EventType.SLEEP, label: 'Sleep', icon: '😴' },
  { type: EventType.BOTTLE, label: 'Bottles', icon: '🍶' },
  { type: EventType.MEDICATION, label: 'Medication', icon: '💊' },
  { type: EventType.WEIGHT, label: 'Weight', icon: '⚖️' },
  { type: EventType.TEMPERATURE, label: 'Temperature', icon: '🌡️' },
  { type: EventType.TUMMY_TIME, label: 'Tummy Time', icon: '🏋️' },
  { type: EventType.PUMP, label: 'Pumping', icon: '🥛' },
  { type: EventType.SPIT_UP, label: 'Spit Up', icon: '💧' },
  { type: EventType.JOURNAL, label: 'Journal', icon: '📝' },
];

const DATE_PRESETS = [
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 3 months', months: 3 },
  { label: 'Last 6 months', months: 6 },
  { label: 'All time', all: true },
];

export default function ExportData() {
  // Baby selection
  const [decryptedBabies, setDecryptedBabies] = useState<DecryptedBaby[]>([]);
  const [selectedBabyId, setSelectedBabyId] = useState<string>('');

  // Export options
  const [exportFormat, setExportFormat] = useState<'pdf' | 'csv'>('pdf');
  const [selectedEventTypes, setSelectedEventTypes] = useState<EventType[]>([]);
  const [datePreset, setDatePreset] = useState<string>('Last 30 days');
  const [customDateFrom, setCustomDateFrom] = useState<string>('');
  const [customDateTo, setCustomDateTo] = useState<string>('');
  const [includeNotes, setIncludeNotes] = useState(true);

  // Export hook
  const { exportData, progress, resetProgress, isExporting } = useExport();

  // Fetch babies
  const { data: babies, isLoading } = useQuery({
    queryKey: ['babies'],
    queryFn: async () => {
      const response = await babyApi.list();
      return response.data || [];
    },
  });

  // Decrypt baby profiles
  useEffect(() => {
    async function decryptBabies() {
      if (!babies) return;

      const decrypted: DecryptedBaby[] = [];
      for (const baby of babies) {
        try {
          const aesKey = await getBabyKey(baby.id);
          if (aesKey) {
            const profile = await decryptBabyProfile(baby.encryptedData, aesKey);
            decrypted.push({
              id: baby.id,
              name: profile.name,
              profileType: (profile.profileType === 'pregnancy' ? 'PREGNANCY' : 'BABY') as ProfileType,
              dateOfBirth: profile.dateOfBirth ? new Date(profile.dateOfBirth) : undefined,
              dueDate: profile.dueDate,
            });
          }
        } catch (err) {
          console.error(`Failed to decrypt baby ${baby.id}:`, err);
        }
      }

      setDecryptedBabies(decrypted);
      if (decrypted.length > 0 && !selectedBabyId) {
        setSelectedBabyId(decrypted[0].id);
      }
    }

    decryptBabies();
  }, [babies, selectedBabyId]);

  // Toggle event type selection
  const toggleEventType = (type: EventType) => {
    setSelectedEventTypes((prev) =>
      prev.includes(type)
        ? prev.filter((t) => t !== type)
        : [...prev, type]
    );
  };

  // Select/deselect all event types
  const selectAllEventTypes = () => {
    if (selectedEventTypes.length === EVENT_TYPES.length) {
      setSelectedEventTypes([]);
    } else {
      setSelectedEventTypes(EVENT_TYPES.map((e) => e.type));
    }
  };

  // Calculate date range from preset
  const getDateRange = (): { dateFrom?: Date; dateTo?: Date } => {
    const now = new Date();

    if (datePreset === 'Custom') {
      return {
        dateFrom: customDateFrom ? startOfDay(new Date(customDateFrom)) : undefined,
        dateTo: customDateTo ? endOfDay(new Date(customDateTo)) : undefined,
      };
    }

    const preset = DATE_PRESETS.find((p) => p.label === datePreset);
    if (!preset) return {};

    if (preset.all) {
      return {};
    }

    const dateTo = endOfDay(now);
    let dateFrom: Date;

    if (preset.days) {
      dateFrom = startOfDay(subDays(now, preset.days));
    } else if (preset.months) {
      dateFrom = startOfDay(subMonths(now, preset.months));
    } else {
      return {};
    }

    return { dateFrom, dateTo };
  };

  // Handle export
  const handleExport = () => {
    const baby = decryptedBabies.find((b) => b.id === selectedBabyId);
    if (!baby) return;

    const { dateFrom, dateTo } = getDateRange();

    exportData({
      babyId: baby.id,
      babyName: baby.name,
      dateOfBirth: baby.dateOfBirth,
      dateFrom,
      dateTo,
      eventTypes: selectedEventTypes.length > 0 ? selectedEventTypes : undefined,
      format: exportFormat,
      includeNotes,
    });
  };

  if (isLoading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
          <div className="h-40 bg-gray-200 dark:bg-gray-700 rounded" />
        </div>
      </div>
    );
  }

  if (decryptedBabies.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="text-center py-16">
          <span className="text-6xl">📊</span>
          <h2 className="mt-4 text-2xl font-bold text-gray-900 dark:text-white">
            No Babies Found
          </h2>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Add a baby profile first to export data
          </p>
          <Link to="/app/baby/new" className="btn-primary mt-6">
            Add Baby Profile
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/app/settings"
          className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 mb-2 inline-block"
        >
          &larr; Back to Settings
        </Link>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Export Data
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Download your baby's data as a PDF report or CSV file
        </p>
      </div>

      {/* Baby Selection */}
      <div className="card p-4 mb-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Select Baby
        </label>
        <div className="flex gap-2 flex-wrap">
          {decryptedBabies.map((baby) => (
            <button
              key={baby.id}
              onClick={() => setSelectedBabyId(baby.id)}
              className={`px-4 py-2 rounded-full transition-colors ${
                selectedBabyId === baby.id
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {baby.name}
            </button>
          ))}
        </div>
      </div>

      {/* Export Format */}
      <div className="card p-4 mb-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Export Format
        </label>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="format"
              value="pdf"
              checked={exportFormat === 'pdf'}
              onChange={() => setExportFormat('pdf')}
              className="text-primary-600"
            />
            <span className="text-gray-900 dark:text-white">
              📄 PDF Report
            </span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="format"
              value="csv"
              checked={exportFormat === 'csv'}
              onChange={() => setExportFormat('csv')}
              className="text-primary-600"
            />
            <span className="text-gray-900 dark:text-white">
              📊 CSV Spreadsheet
            </span>
          </label>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
          {exportFormat === 'pdf'
            ? 'Best for sharing with healthcare providers'
            : 'Best for data analysis in Excel or Google Sheets'}
        </p>
      </div>

      {/* Date Range */}
      <div className="card p-4 mb-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Date Range
        </label>
        <div className="flex gap-2 flex-wrap mb-3">
          {DATE_PRESETS.map((preset) => (
            <button
              key={preset.label}
              onClick={() => setDatePreset(preset.label)}
              className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                datePreset === preset.label
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {preset.label}
            </button>
          ))}
          <button
            onClick={() => setDatePreset('Custom')}
            className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
              datePreset === 'Custom'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            Custom
          </button>
        </div>

        {datePreset === 'Custom' && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                From
              </label>
              <input
                type="date"
                value={customDateFrom}
                onChange={(e) => setCustomDateFrom(e.target.value)}
                className="input"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                To
              </label>
              <input
                type="date"
                value={customDateTo}
                onChange={(e) => setCustomDateTo(e.target.value)}
                className="input"
              />
            </div>
          </div>
        )}
      </div>

      {/* Event Types Filter */}
      <div className="card p-4 mb-4">
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Event Types
          </label>
          <button
            onClick={selectAllEventTypes}
            className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
          >
            {selectedEventTypes.length === EVENT_TYPES.length
              ? 'Deselect all'
              : 'Select all'}
          </button>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
          {selectedEventTypes.length === 0
            ? 'All event types will be included'
            : `${selectedEventTypes.length} type${selectedEventTypes.length !== 1 ? 's' : ''} selected`}
        </p>
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {EVENT_TYPES.map(({ type, label, icon }) => (
            <button
              key={type}
              onClick={() => toggleEventType(type)}
              className={`flex flex-col items-center p-2 rounded-lg text-xs transition-colors ${
                selectedEventTypes.includes(type)
                  ? 'bg-primary-100 dark:bg-primary-900/30 ring-2 ring-primary-500'
                  : selectedEventTypes.length === 0
                  ? 'bg-gray-50 dark:bg-gray-800'
                  : 'bg-gray-50 dark:bg-gray-800 opacity-50'
              }`}
            >
              <span className="text-lg">{icon}</span>
              <span className="mt-1 text-gray-700 dark:text-gray-300">
                {label}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Options */}
      {exportFormat === 'csv' && (
        <div className="card p-4 mb-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={includeNotes}
              onChange={(e) => setIncludeNotes(e.target.checked)}
              className="w-5 h-5 text-primary-600 rounded"
            />
            <span className="text-gray-900 dark:text-white">
              Include notes in export
            </span>
          </label>
        </div>
      )}

      {/* Progress */}
      {(progress.stage !== 'idle') && (
        <div className="card p-4 mb-4">
          <div className="flex items-center gap-3">
            {progress.stage === 'done' ? (
              <span className="text-2xl">✅</span>
            ) : progress.stage === 'error' ? (
              <span className="text-2xl">❌</span>
            ) : (
              <div className="animate-spin w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full" />
            )}
            <div className="flex-1">
              <p className="font-medium text-gray-900 dark:text-white">
                {progress.message}
              </p>
              {progress.total > 0 && progress.stage === 'decrypting' && (
                <div className="mt-2">
                  <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary-600 transition-all duration-300"
                      style={{
                        width: `${(progress.current / progress.total) * 100}%`,
                      }}
                    />
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {progress.current} of {progress.total}
                  </p>
                </div>
              )}
            </div>
          </div>
          {(progress.stage === 'done' || progress.stage === 'error') && (
            <button
              onClick={resetProgress}
              className="mt-3 text-sm text-primary-600 dark:text-primary-400 hover:underline"
            >
              Export another file
            </button>
          )}
        </div>
      )}

      {/* Export Button */}
      {progress.stage === 'idle' && (
        <button
          onClick={handleExport}
          disabled={isExporting || !selectedBabyId}
          className="btn-primary w-full"
        >
          {isExporting ? (
            <span className="flex items-center justify-center gap-2">
              <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
              Exporting...
            </span>
          ) : (
            `Export as ${exportFormat.toUpperCase()}`
          )}
        </button>
      )}

      {/* Privacy Note */}
      <p className="text-sm text-gray-500 dark:text-gray-400 text-center mt-4">
        🔒 All data is decrypted locally on your device. No data is sent to our servers.
      </p>
    </div>
  );
}
